import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Calendar, ArrowRight, Clock, User, Loader2, BookOpen } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Fallback posts if API returns empty
const FALLBACK_POSTS = [
  {
    id: "1",
    slug: "understanding-tdsr-bahamas",
    title: "Understanding TDSR: What Every Bahamian Borrower Should Know",
    excerpt: "Total Debt Service Ratio (TDSR) is one of the most important factors banks consider when evaluating your loan application. Learn how to calculate yours and what banks look for.",
    category: "Credit Education",
    created_at: "2026-02-28T00:00:00Z",
    author: "Financify Team"
  },
  {
    id: "2",
    slug: "first-time-home-buyer-bahamas",
    title: "First-Time Home Buyer's Guide to Mortgages in The Bahamas",
    excerpt: "Buying your first home is exciting but can be overwhelming. This guide covers everything you need to know about mortgage options, down payments, and the application process.",
    category: "Mortgage Basics",
    created_at: "2026-02-25T00:00:00Z",
    author: "Financify Team"
  },
  {
    id: "3",
    slug: "improve-loan-approval-chances",
    title: "5 Ways to Improve Your Loan Approval Chances",
    excerpt: "Getting approved for a loan isn't just about income. Learn practical steps you can take to strengthen your application and increase your chances of approval.",
    category: "Loan Tips",
    created_at: "2026-02-20T00:00:00Z",
    author: "Financify Team"
  },
  {
    id: "4",
    slug: "debt-consolidation-guide",
    title: "Is Debt Consolidation Right for You?",
    excerpt: "If you're juggling multiple loans and credit card payments, debt consolidation might help. Learn the pros, cons, and when it makes sense.",
    category: "Loan Tips",
    created_at: "2026-02-15T00:00:00Z",
    author: "Financify Team"
  },
  {
    id: "5",
    slug: "government-vs-private-sector-loans",
    title: "Government vs Private Sector: How Employment Affects Your Loan",
    excerpt: "Your employment type significantly impacts your loan eligibility and terms. Understand the differences and what banks are looking for.",
    category: "Credit Education",
    created_at: "2026-02-10T00:00:00Z",
    author: "Financify Team"
  },
  {
    id: "6",
    slug: "car-loan-new-vs-used",
    title: "New vs Used: Choosing the Right Car Loan in The Bahamas",
    excerpt: "The financing options for new and used vehicles differ significantly. Learn about interest rates, terms, and requirements for each.",
    category: "Auto Loans",
    created_at: "2026-02-05T00:00:00Z",
    author: "Financify Team"
  }
];

export default function BlogPage() {
  const [posts, setPosts] = useState([]);
  const [categories, setCategories] = useState(["All"]);
  const [selectedCategory, setSelectedCategory] = useState("All");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchPosts = async () => {
      try {
        const [postsRes, categoriesRes] = await Promise.all([
          axios.get(`${API}/blog/posts`),
          axios.get(`${API}/blog/categories`)
        ]);
        
        if (postsRes.data && postsRes.data.length > 0) {
          setPosts(postsRes.data);
        } else {
          setPosts(FALLBACK_POSTS);
        }
        
        if (categoriesRes.data && categoriesRes.data.length > 1) {
          setCategories(categoriesRes.data);
        } else {
          // Extract categories from fallback posts
          const fallbackCategories = [...new Set(FALLBACK_POSTS.map(p => p.category))];
          setCategories(["All", ...fallbackCategories]);
        }
      } catch (error) {
        console.error("Failed to fetch blog posts:", error);
        setPosts(FALLBACK_POSTS);
        const fallbackCategories = [...new Set(FALLBACK_POSTS.map(p => p.category))];
        setCategories(["All", ...fallbackCategories]);
      } finally {
        setLoading(false);
      }
    };
    
    fetchPosts();
  }, []);

  const filteredPosts = selectedCategory === "All" 
    ? posts 
    : posts.filter(post => post.category === selectedCategory);

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      month: 'long', 
      day: 'numeric', 
      year: 'numeric' 
    });
  };

  const estimateReadTime = (content) => {
    if (!content) return "3 min read";
    const words = content.split(/\s+/).length;
    const minutes = Math.ceil(words / 200);
    return `${minutes} min read`;
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero */}
      <section className="py-16 md:py-20" style={{ background: 'linear-gradient(135deg, #f8fffe 0%, #ffffff 100%)' }}>
        <div className="container-main">
          <div className="max-w-3xl mx-auto text-center">
            <h1 className="text-4xl md:text-5xl font-bold mb-6" style={{ color: '#2D3748' }}>
              Financify Blog
            </h1>
            <p className="text-lg text-gray-600">
              Financial education, loan tips, and insights for Bahamian borrowers
            </p>
          </div>
        </div>
      </section>

      {/* Blog Content */}
      <section className="py-16">
        <div className="container-main">
          {/* Category Filter */}
          <div className="flex flex-wrap justify-center gap-2 mb-12">
            {categories.map((category) => (
              <button
                key={category}
                onClick={() => setSelectedCategory(category)}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                  selectedCategory === category 
                    ? 'bg-[#52BFB8] text-white' 
                    : 'bg-white text-gray-600 hover:bg-gray-100 border border-gray-200'
                }`}
                data-testid={`category-${category.toLowerCase().replace(/\s+/g, '-')}`}
              >
                {category}
              </button>
            ))}
          </div>

          {/* Loading State */}
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <Loader2 className="w-8 h-8 animate-spin text-[#52BFB8]" />
            </div>
          ) : filteredPosts.length === 0 ? (
            <div className="text-center py-20">
              <BookOpen className="w-12 h-12 mx-auto mb-4 text-gray-300" />
              <p className="text-gray-500">No posts found in this category.</p>
            </div>
          ) : (
            /* Blog Grid */
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-6xl mx-auto">
              {filteredPosts.map((post) => (
                <article 
                  key={post.id}
                  className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden hover:shadow-md transition-shadow"
                  data-testid={`blog-post-${post.slug}`}
                >
                  {/* Featured Image or Placeholder */}
                  <div className="h-48 bg-gradient-to-br from-[#52BFB8]/20 to-[#FDD552]/20 flex items-center justify-center overflow-hidden">
                    {post.featured_image ? (
                      <img 
                        src={post.featured_image} 
                        alt={post.title}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <span className="text-4xl opacity-50">📝</span>
                    )}
                  </div>
                  
                  <div className="p-6">
                    <div className="flex items-center gap-2 mb-3">
                      <span 
                        className="text-xs font-medium px-2 py-1 rounded-full"
                        style={{ background: 'rgba(82, 191, 184, 0.15)', color: '#3DA8A1' }}
                      >
                        {post.category}
                      </span>
                    </div>
                    
                    <h2 className="text-lg font-bold mb-2 line-clamp-2" style={{ color: '#2D3748' }}>
                      {post.title}
                    </h2>
                    
                    <p className="text-gray-600 text-sm mb-4 line-clamp-3">
                      {post.excerpt}
                    </p>
                    
                    <div className="flex items-center justify-between text-xs text-gray-500">
                      <div className="flex items-center gap-3">
                        <span className="flex items-center gap-1">
                          <Calendar className="w-3 h-3" />
                          {formatDate(post.created_at || post.published_at)}
                        </span>
                        <span className="flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          {estimateReadTime(post.content)}
                        </span>
                      </div>
                    </div>
                    
                    <Link 
                      to={`/blog/${post.slug}`}
                      className="inline-flex items-center gap-1 mt-4 text-sm font-medium text-[#52BFB8] hover:text-[#3DA8A1] transition-colors"
                      data-testid={`read-more-${post.slug}`}
                    >
                      Read More
                      <ArrowRight className="w-4 h-4" />
                    </Link>
                  </div>
                </article>
              ))}
            </div>
          )}

          {/* Resources Section */}
          <div className="max-w-4xl mx-auto mt-16">
            <h2 className="text-2xl font-bold mb-6 text-center" style={{ color: '#2D3748' }}>
              Additional Resources
            </h2>
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-8">
              <p className="text-gray-600 mb-4">
                For official information about banking and financial regulations in The Bahamas, 
                visit these trusted sources:
              </p>
              <ul className="space-y-3">
                <li>
                  <a 
                    href="https://www.centralbankbahamas.com" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-[#52BFB8] hover:underline"
                  >
                    Central Bank of The Bahamas
                  </a>
                  <span className="text-gray-500 text-sm ml-2">- Official banking regulator</span>
                </li>
                <li>
                  <a 
                    href="https://www.bisxbahamas.com" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-[#52BFB8] hover:underline"
                  >
                    Bahamas International Securities Exchange (BISX)
                  </a>
                  <span className="text-gray-500 text-sm ml-2">- Securities and investments</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
